import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  type PopoverOrigin,
} from '@mui/material';
import { ExitToApp, Logout, Person } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { useToastr } from '@farmshare/ui-components';
import { isBuyerOnly } from 'lib/userHelpers';
import { userState } from 'state';
import { useCognitoAuth } from '../../auth/cognito-auth-context';

/** 48px is the touch-target floor; 44 is the desktop pointer equivalent. */
const MENU_ITEM_SX = {
  minHeight: { xs: 48, sm: 44 },
  px: 2,
  gap: 0.5,
} as const;

const MENU_ICON_SX = { minWidth: 36, color: 'text.secondary' } as const;

const MENU_LABEL_PROPS = { fontSize: '0.95rem', fontWeight: 500 } as const;

interface ProfileMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onLogout: () => void;
  /**
   * Called when a menu item navigates away. `onClose` only dismisses this
   * popover, so a host that is itself an overlay (the mobile nav drawer) would
   * otherwise stay open on top of the page it just navigated to.
   */
  onNavigate?: () => void;
  /**
   * Size the menu to its anchor rather than to its own text.
   *
   * In the mobile drawer the anchor is the full-width Profile row, so the
   * menu lands the same width as the drawer and reads as part of it — a
   * text-width card floating over a full-bleed menu reads as a desktop
   * control that wandered onto a phone. The rail's anchor is a 64px icon
   * button, where matching the anchor would be absurd, so it stays off there.
   */
  matchAnchorWidth?: boolean;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
}

export function ProfileMenu({
  anchorEl,
  onClose,
  onLogout,
  onNavigate,
  matchAnchorWidth = false,
  anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
  transformOrigin = { vertical: 'bottom', horizontal: 'left' },
}: ProfileMenuProps) {
  const navigate = useNavigate();
  const user = useRecoilValue(userState);
  const { impersonateUserId, clearImpersonation } = useCognitoAuth();
  const { push: pushToast } = useToastr();

  // Buyer-only users get the dedicated buyer account page; everyone else keeps
  // the shared (vendor/tenant-aware) profile page.
  const profilePath = user && isBuyerOnly(user) ? '/buyer/account' : '/profile';

  // Read on render rather than measured in an effect: the popover only ever
  // mounts with an anchor already attached, so the width is known by then.
  const anchorWidth = matchAnchorWidth ? anchorEl?.offsetWidth : undefined;

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      slotProps={{
        paper: {
          sx: {
            // No left offset when matching the anchor — the margin would
            // push the menu that far past the anchor's right edge.
            ml: matchAnchorWidth ? 0 : 1,
            borderRadius: 2,
            // A menu of two items had been sized like a desktop context
            // menu: `dense` rows on a ~140px card, which is a ~32px tap
            // target on the surface where this menu is used MOST — it is the
            // only way out of the mobile drawer. Give it a real width and
            // let the rows below take a thumb.
            minWidth: { xs: 220, sm: 200 },
            ...(matchAnchorWidth && anchorWidth
              ? { width: anchorWidth, maxWidth: 'none' }
              : {}),
          },
        },
      }}
    >
      {/* Not `dense`: see the paper's note. */}
      <MenuList sx={{ py: 1 }}>
        <MenuItem
          onClick={() => {
            onClose();
            onNavigate?.();
            navigate(profilePath);
          }}
          sx={MENU_ITEM_SX}
        >
          <ListItemIcon sx={MENU_ICON_SX}>
            <Person />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={MENU_LABEL_PROPS}>
            Account
          </ListItemText>
        </MenuItem>
        {impersonateUserId && (
          <MenuItem
            onClick={() => {
              onClose();
              clearImpersonation();
              pushToast({
                title: 'Stopped impersonating user',
                body: 'You are now back to your admin account',
                bg: 'info',
              });
              window.location.reload();
            }}
            sx={MENU_ITEM_SX}
          >
            <ListItemIcon sx={MENU_ICON_SX}>
              <ExitToApp />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={MENU_LABEL_PROPS}>
              Stop Impersonating
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            onClose();
            onLogout();
          }}
          sx={MENU_ITEM_SX}
        >
          <ListItemIcon sx={MENU_ICON_SX}>
            <Logout />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={MENU_LABEL_PROPS}>
            Logout
          </ListItemText>
        </MenuItem>
      </MenuList>
    </Popover>
  );
}
